/* eslint-disable no-cond-assign */

import { ProposalDAOInterface } from '../../ProposalDAO';
import { ComparisonItemBasedProposalDAO } from '../../ProposalDAOImpl';
import { ProposalFieldDAO } from '../../ProposalFieldDAO';
import { ComparisonItemDAO } from '../../ProposalItemDAO';
import { DataPoints, DataPointResponse, ESGTemperatureDetails } from '../../DataPoints';
import * as ProposalNS from '../../../Proposal';
import * as ProposalElementNS from '../../../ProposalElements';
import DonutData from '@/types/donut-data';
import ESGAnalytics from '@/types/esg-analytics';

/**
 * @class ESG Implied Temperature Rise Data Access Object
 */
export default class ESGImpliedTemperatureRiseDAO extends ComparisonItemBasedProposalDAO {
  out: DonutData | null = null;

  esgData: ESGAnalytics | undefined = undefined;

  public static buildInstance(proposal: ProposalNS.Proposal, proposalElement: ProposalElementNS.ProposalDataElement): ProposalDAOInterface {
    return new ESGImpliedTemperatureRiseDAO(proposal, proposalElement);
  }

  /* eslint-disable-next-line class-methods-use-this */
  protected getColorKeys(): string[] {
    return ['ESG_IMPLIED_TEMPERATURE_RISE_COLORS'];
  }

  protected build(): void {
    let itemDAO = null;
    while ((itemDAO = this.nextItem()) !== null) {
      this.esgData = itemDAO.item.esg;
      this.setImpliedTemperatureRise(itemDAO);
    }
  }

  getDonutData(index: number = 0): DonutData | null {
    this.out = super.getDonutData(index);

    if (!this.out) {
      return this.out;
    }

    if (this.colorsDict) {
      this.out.colorsDict = this.colorsDict;
    }

    const { configOptions } = this.proposalElement.dataView as ProposalNS.DonutViewOptions;

    if (configOptions.title) {
      const item: ComparisonItemDAO = this.getItemDAOs()[index];
      const { response }: DataPointResponse<ESGTemperatureDetails> = DataPoints.getESGImpliedTemperatureRiseData(item.item);
      const label = this.formatMessage(`ESGComparisonTool.ImpliedTemperatureRise.TemperatureRanking.${response.key}`);

      this.out.title = {
        ...configOptions.title,
        text: configOptions.title.text
          .replace(
            '{{value}}',
            response.value
              ? `<span class="value">${response.value.toFixed(1)}</span> <span class="unit">°C</span>`
              : `<span class="na">${this.formatMessage('Common.NotApplicable')}</span>`,
          )
          .replace('{{label}}', label)
          .replace('{{color}}', this.colorsDict ? this.colorsDict[response.key] : ''),
      };
    }

    return this.out;
  }

  // private methods

  private setImpliedTemperatureRise(itemDAO: ComparisonItemDAO, maxItems = 3) {
    const { response, disclaimerKeys }: DataPointResponse<ESGTemperatureDetails> = DataPoints.getESGImpliedTemperatureRiseData(itemDAO.item);
    const label = this.formatMessage(`ESGComparisonTool.ImpliedTemperatureRise.TemperatureRanking.${response.key}`);
    this.addDisclaimerKeys(disclaimerKeys, itemDAO.item);

    if (response.transposedValue) {
      const temperatureField = new ProposalFieldDAO(label, response.transposedValue);
      temperatureField.key = response.key;
      this.addFieldDAO(itemDAO, temperatureField);
      for (let i = 0; i < maxItems - response.transposedValue; i++) {
        const naField = new ProposalFieldDAO(this.formatMessage('ESGComparisonTool.ImpliedTemperatureRise.TemperatureRanking.UNAVAILABLE'), 1);
        naField.key = 'UNAVAILABLE';
        this.addFieldDAO(itemDAO, naField);
      }
    } else {
      const naField = new ProposalFieldDAO(this.formatMessage('ESGComparisonTool.ImpliedTemperatureRise.TemperatureRanking.UNAVAILABLE'), maxItems);
      naField.key = 'UNAVAILABLE';
      this.addFieldDAO(itemDAO, naField);
    }
  }
}
